











































































































































































































































































import { Component, Prop, Watch } from "vue-property-decorator";

import * as screenfull from "screenfull";

import { fmtDegLat, fmtDegLon, fmtHours } from "@wwtelescope/astro";
import { Folder, FolderUp, Place, Imageset } from "@wwtelescope/engine";
import { ImageSetType } from "@wwtelescope/engine-types";
import {
  SetupForImagesetOptions,
  WWTAwareComponent,
} from "@wwtelescope/engine-vuex";
import { CreditMode, EmbedSettings } from "@wwtelescope/embed-common";
import { Meta } from "@sophosoft/vue-meta-decorator";

/** The overall state of the WWT embed component. */
enum ComponentState {
  /** Waiting for resources and/or content to load. Not yet ready to start. */
  LoadingResources,

  /** Resources have loaded. We can start when the user activates us. */
  ReadyToStart,

  /** The user has activated us. */
  Started,
}

type ToolType = "crossfade" | "choose-background" | "playback-controls" | null;

class BackgroundImageset {
  public imagesetName: string;
  public displayName: string;

  constructor(displayName: string, imagesetName: string) {
    this.displayName = displayName;
    this.imagesetName = imagesetName;
  }
}

const skyBackgroundImagesets: BackgroundImageset[] = [
  new BackgroundImageset(
    "Optical (Terapixel DSS)",
    "Digitized Sky Survey (Color)"
  ),
	new BackgroundImageset(
    "PanSTARRS1 3Pi (Optical)",
    "PanSTARRS1 3Pi"
  ),
	new BackgroundImageset(
    "NASA Deep Star Maps (Optical)",
    "NASA SVS Deep Star Maps 2020 (Synthetic, Optical)"
  ),
  new BackgroundImageset(
    "Low-frequency radio (VLSS)",
    "VLSS: VLA Low-frequency Sky Survey (Radio)"
  ),
  new BackgroundImageset("Infrared (2MASS)", "2Mass: Imagery (Infrared)"),
  new BackgroundImageset("GLIMPSE 360 (IR)", "GLIMPSE 360"),
  new BackgroundImageset("Ultraviolet (GALEX)", "GALEX (Ultraviolet)"),
  new BackgroundImageset(
    "X-Ray (ROSAT RASS)",
    "RASS: ROSAT All Sky Survey (X-ray)"
  ),
  new BackgroundImageset(
    "Gamma Rays (FERMI LAT 8-year)",
    "Fermi LAT 8-year (gamma)"
  ),
];

type Shape = { width: number; height: number };
const defaultWindowShape: Shape = { width: 1200, height: 900 };

type WwtComponentLayout = { top: string; height: string };

export type FolderItem = Folder | FolderUp | Imageset | Place;

@Component
export default class Embed extends WWTAwareComponent {
  CreditMode = CreditMode;

  @Prop({ default: new EmbedSettings() })
  readonly embedSettings!: EmbedSettings;
  @Prop({ default: "" }) jwstWtmlUrl!: string;
  @Prop({ default: "" }) url!: string;
  @Prop({ default: "" }) thumbnailUrl!: string;
  @Prop({ default: "" }) bgName!: string;

  componentState = ComponentState.LoadingResources;
  backgroundImagesets: BackgroundImageset[] = [];
  currentTool: ToolType = "crossfade";
  fullscreenModeActive = false;
  tourPlaybackJustEnded = false;
  windowShape = defaultWindowShape;
	
	collectionFolder: Folder | null = null;

  // This is the Tweet text:
  description =
    "Interactively explore a collection of the James Webb Space Telescope astroimagery in context of the sky!";
  hashtags = ["jwst", "unfoldtheuniverse", "1yearofwebb", "wwt"];

  get hashtagString() {
    return this.hashtags.join(",");
  }

  networks = [
    { name: "facebook", color: "#1877f2", text: "Share" },
    { name: "twitter", color: "#1da1f2", text: "Tweet" },
  ];

  get showFolderView() {
    const children = this.collectionFolder?.get_children();
    return children !== null && children !== undefined && children.length > 1;
    //return false;
  }

  get isLoadingState() {
    return this.componentState == ComponentState.LoadingResources;
  }

  get isReadyToStartState() {
    return this.componentState == ComponentState.ReadyToStart;
  }

  showImageDescription = true;
	showImageName = false;

  toggleImageDescription() {
    this.showImageDescription = !this.showImageDescription;
  }
	
	toggleImageName() {
    this.showImageName = !this.showImageName;
  }

  openSourceLink() {
    // Custom link to the target URL so that we can count clicks.
    window.open("https://bit.ly/wwt-rcip");
  }

  get coordText() {
    if (this.wwtRenderType == ImageSetType.sky) {
      return `${fmtHours(this.wwtRARad)} ${fmtDegLat(this.wwtDecRad)}`;
    }

    return `${fmtDegLon(this.wwtRARad)} ${fmtDegLat(this.wwtDecRad)}`;
  }

	get curForegroundImagesetName() {
    if (this.wwtForegroundImageset == null) return "";
    return this.wwtForegroundImageset.get_name();
		
  }

  get curBackgroundImagesetName() {
    if (this.wwtBackgroundImageset == null) return "";
    return this.wwtBackgroundImageset.get_name();
  }

  set curBackgroundImagesetName(name: string) {
    this.setBackgroundImageByName(name);
  }

  get foregroundOpacity() {
    return this.wwtForegroundOpacity;
  }

  set foregroundOpacity(o: number) {
    this.setForegroundOpacity(o);
  }

  get fullscreenAvailable() {
    return screenfull.isEnabled;
  }

  get showBackgroundChooser() {
    if (this.wwtIsTourPlaying) return false;

    // TODO: we should wire in choices for other modes!
    return this.wwtRenderType == ImageSetType.sky;
  }

  get showCrossfader() {
    if (this.wwtIsTourPlaying) return false; // maybe show this if tour player is active but not playing?

    if (
      this.wwtForegroundImageset == null ||
      this.wwtForegroundImageset === undefined
    )
      return false;

    return this.wwtForegroundImageset != this.wwtBackgroundImageset;
  }

  get showPlaybackControls() {
    return this.wwtIsTourPlayerActive && !this.wwtIsTourPlaying;
  }

  get showToolMenu() {
    // This should return true if there are any tools to show.
    return (
      this.showBackgroundChooser ||
      this.showCrossfader ||
      this.showPlaybackControls
    );
  }

  created() {
    let prom = this.waitForReady().then(() => {
      for (const s of this.embedSettings.asSettings()) {
        this.applySetting(s);
      }
    });

    if (this.embedSettings.tourUrl.length) {
      prom = prom.then(async () => {
        // TODO: figure out a good thing to do here
        this.backgroundImagesets = [...skyBackgroundImagesets];

        await this.loadTour({
          url: this.embedSettings.tourUrl,
          play: false,
        });

        this.componentState = ComponentState.ReadyToStart;
        this.setTourPlayerLeaveSettingsWhenStopped(true);
        this.currentTool = "playback-controls";
      });
    } else {
      // Many more possibilities if we're not playing a tour ...
      if (this.embedSettings.wtmlUrl.length) {
        prom = prom.then(async () => {
          const folder = await this.loadImageCollection({
            url: this.embedSettings.wtmlUrl,
            loadChildFolders: false,
          });

          if (this.embedSettings.wtmlPlace) {
            // Currently, there is an issue with the `places` field of a `Folder`
            // object populating correctly. Thus, we iterate over `children` instead
            for (const pl of folder.get_children() ?? []) {
              if (!(pl instanceof Place)) {
                continue;
              }
              if (pl.get_name() == this.embedSettings.wtmlPlace) {
                /* This is nominally an async Action, but with `instant: true` it's ... instant */
                this.gotoTarget({
                  place: pl,
                  noZoom: false,
                  instant: true,
                  trackObject: true,
                });
              }
            }
          }
        });
      }

      prom.then(() => {
        // setupForImageset() will apply a default background that is appropriate
        // for the foreground, but we want to be able to override it.

        let backgroundWasInitialized = false;
        let bgName = this.embedSettings.backgroundImagesetName;

        if (this.embedSettings.foregroundImagesetName.length) {
          const img = this.lookupImageset(
            this.embedSettings.foregroundImagesetName
          );

          if (img !== null) {
            // If the imageset is a panorama, we want to set it to be the background
            const isPanorama = img.get_dataSetType() == ImageSetType.panorama;
            const options: SetupForImagesetOptions = { foreground: img };
            if (isPanorama) {
              options.background = img;
              backgroundWasInitialized = true;
            }

            // For setup of planetary modes to work, we need to pass the specified
            // background imageset to setupForImageset().
            // For a panorama, we've already set the imageset itself as the background
            if (!isPanorama && bgName.length) {
              const bkg = this.lookupImageset(bgName);
              if (bkg !== null) {
                options.background = bkg;
                backgroundWasInitialized = true;
              }
            }

            this.setupForImageset(options);
          }
        }
				
				this.loadImageCollection({
          url: this.jwstWtmlUrl,
          loadChildFolders: true,
        }).then((folder) => {
          this.curBackgroundImagesetName = this.bgName;
          this.backgroundImagesets.unshift(
            new BackgroundImageset("unWISE (IR)","unWISE color, from W2 and W1 bands")
          );
				});
				
				/*
        this.loadImageCollection({
          url: this.jwstWtmlUrl,
          loadChildFolders: true,
        }).then((folder) => {
          this.curBackgroundImagesetName = this.bgName;
          this.backgroundImagesets.unshift(
            new BackgroundImageset("JWST - Pillars of Creation (NIRCam)", "Webb Takes a Stunning, Star-Filled Portrait of the Pillars of Creation")
          );
				});
				
				this.loadImageCollection({
          url: this.jwstWtmlUrl,
          loadChildFolders: true,
        }).then((folder) => {
          this.curBackgroundImagesetName = this.bgName;
          this.backgroundImagesets.unshift(
            new BackgroundImageset("JWST - Pillars of Creation (MIRI)", "Pillars of Creation (MIRI Image)")
          );
				});
				
				this.loadImageCollection({
          url: this.jwstWtmlUrl,
          loadChildFolders: true,
        }).then((folder) => {
          this.curBackgroundImagesetName = this.bgName;
          this.backgroundImagesets.unshift(
            new BackgroundImageset("HST - Pillars of Creation (Visible)", "New Hubble View of the Pillars of Creation — Visible")
          );
				});
				
				this.loadImageCollection({
          url: this.jwstWtmlUrl,
          loadChildFolders: true,
        }).then((folder) => {
          this.curBackgroundImagesetName = this.bgName;
          this.backgroundImagesets.unshift(
            new BackgroundImageset("HST - Pillars of Creation (Infrared)", "New Hubble View of the Pillars of Creation — Infrared")
          );
				});
				*/
				this.loadImageCollection({
          url: this.jwstWtmlUrl,
          loadChildFolders: true,
        }).then((folder) => {
          this.collectionFolder = folder;
          const children = folder.get_children();
          if (children === null) {
            return;
          }

          for (const item of children) {
            if (item instanceof Place) {
              this.gotoTarget({
                place: item,
                noZoom: false,
                instant: false,
                trackObject: true,
              });
              break;
            }
          }
        });

        if (!backgroundWasInitialized) {
          if (!bgName.length) {
            // Empty bgname implies that we should choose a default background. If
            // setupForImageset() didn't do that for us, go with:
            bgName = "Digitized Sky Survey (Color)";
          }

          this.setBackgroundImageByName(bgName);
        }

        // TODO: DTRT in different modes.
        this.backgroundImagesets = [...skyBackgroundImagesets];
        let foundBG = false;

        for (const bgi of this.backgroundImagesets) {
          if (bgi.imagesetName == bgName) {
            foundBG = true;
            break;
          }
        }

        if (!foundBG) {
          this.backgroundImagesets.unshift(
            new BackgroundImageset(bgName, bgName)
          );
        }

        this.componentState = ComponentState.Started;
      });
    }
  }

  mounted() {
    if (screenfull.isEnabled) {
      screenfull.on("change", this.onFullscreenEvent);
    }

    window.addEventListener("resize", this.onResizeEvent);
    // ResizeObserver not yet in TypeScript but we should start using it when
    // available. If we're in an iframe, our shape might change spontaneously.
    // const ro = new ResizeObserver(entries => this.onResizeEvent());
    // ro.observer(this.$el);
    this.onResizeEvent();
  }

  destroyed() {
    if (screenfull.isEnabled) {
      screenfull.off("change", this.onFullscreenEvent);
    }

    window.removeEventListener("resize", this.onResizeEvent);
  }

  selectTool(name: ToolType) {
    if (this.currentTool == name) {
      this.currentTool = null;
    } else {
      this.currentTool = name;
    }
  }

  doZoom(zoomIn: boolean) {
    if (zoomIn) {
      this.zoom(1 / 1.3);
    } else {
      this.zoom(1.3);
    }
  }

  toggleFullscreen() {
    if (screenfull.isEnabled) {
      screenfull.toggle();
    }
  }

  onFullscreenEvent() {
    // NB: we need the isEnabled check to make TypeScript happy even though it
    // is not necessary in practice here.
    if (screenfull.isEnabled) {
      this.fullscreenModeActive = screenfull.isFullscreen;
    }
  }

  onResizeEvent() {
    const width = this.$el.clientWidth;
    const height = this.$el.clientHeight;

    if (width > 0 && height > 0) {
      this.windowShape = { width, height };
    } else {
      this.windowShape = defaultWindowShape;
    }
  }

  startInteractive() {
    this.componentState = ComponentState.Started;

    if (this.embedSettings.tourUrl.length) {
      this.startTour();
    }
  }

  get tourPlaybackIcon() {
    if (this.tourPlaybackJustEnded) {
      return "undo-alt";
    }

    if (this.wwtIsTourPlaying) {
      return "pause";
    }

    return "play";
  }

  tourPlaybackButtonClicked() {
    if (this.wwtIsTourPlayerActive) {
      // If we're playing and our window is tall, we have styling active that
      // keeps the WWT widget in a widescreen-ish format to preserve tour
      // layout. If we're stopping playback, this styling will go away. Since
      // the WWT widget tracks its view height as an angular size, we need to
      // tweak it in order to preserve continuity when the tour is paused. This
      // isn't 100% necessary but is cool when it works. It doesn't work if the
      // pause occurs in a very zoomed-out state, where we can't zoom out any
      // farther because we hit the zoom clamps.
      //
      // Keep this in sync with wwtComponentLayout.
      //
      // TODO: make sure this works in 3D mode.
      let newView = null;

      if (this.wwtIsTourPlaying && this.wwtComponentLayout.top != "0") {
        const curHeight = this.windowShape.width * 0.75;
        newView = {
          raRad: 1.0 * this.wwtRARad,
          decRad: 1.0 * this.wwtDecRad,
          zoomDeg: (this.wwtZoomDeg * this.windowShape.height) / curHeight,
          instant: true,
        };
      }

      if (this.tourPlaybackJustEnded) {
        // Restart from beginning. (seekToTourTimecode() starts playback.)
        this.seekToTourTimecode(0);
        this.tourPlaybackJustEnded = false;
      } else {
        this.toggleTourPlayPauseState();
      }

      if (newView !== null) {
        this.gotoRADecZoom(newView);
      }
    }
  }

  formatTimecode(seconds: number): string {
    if (seconds < 0) return "-:--";

    const minutes = Math.floor(seconds / 60);
    seconds = Math.round(seconds - 60 * minutes);
    return minutes.toString() + ":" + seconds.toString().padStart(2, "0");
  }

  get twoWayTourTimecode() {
    return this.wwtTourTimecode;
  }

  set twoWayTourTimecode(code: number) {
    this.seekToTourTimecode(code);
    this.tourPlaybackJustEnded = false;
  }

	@Watch("curForegroundImagesetName")
	onCurForegroundImagesetNameChanged()
	{
		if(this.curForegroundImagesetName != null){
			this.showImageName = true;
		}
	}
	
	@Watch("curBackgroundImagesetName")
	onCurBackgroundImagesetNameChanged()
	{
		if(this.curBackgroundImagesetName != null){
			this.currentTool = "crossfade";
		}
	}

  @Watch("wwtTourCompletions")
  onTourCompletionsChanged(_count: number) {
    this.tourPlaybackJustEnded = true;
  }

  // This property is used to achieve a widescreen effect when playing tours
  // back on a portrait-mode screen, such as on a mobile device. Tours have to
  // be designed with a target screen aspect ratio in mind, so without the
  // widescreen effect the content will get cut off.
  //
  // Keep this in sync with toggleTourPlayback.
  get wwtComponentLayout(): WwtComponentLayout {
    if (this.wwtIsTourPlaying) {
      if (this.windowShape.height > this.windowShape.width) {
        const wwtHeight = this.windowShape.width * 0.75; // => 4:3 aspect ratio
        const height = wwtHeight + "px";
        const top = 0.5 * (this.windowShape.height - wwtHeight) + "px";
        return { top, height };
      }
    }
    return { top: "0", height: "100%" };
  }
}
